import { SubmitHelpers } from '@area2k/use-form';
import { useState, useEffect } from 'react';

import Button from '@/components/Button';
import Stack from '@/components/Stack';
import { MAX_LENGTH_SHIFT_SUPERVISOR_INSTRUCTIONS } from '@/constants/text';
import Form from '@/form';
import ItemSelectField from '@/form/ItemSelectField';
import OptionField from '@/form/OptionField';
import TextField from '@/form/TextField';
import MaskedInputField from '@/form/MaskedInputField';
import TextAreaField from '@/form/TextAreaField';
import {
  useUpdateJobMutation,
  useListJobCustomerAdminsLazyQuery,
} from '@/graphql';
import {
  getJobValues,
  isJobFieldEditable,
  jobScreenField,
} from '@/routes/Agency/Job/util';
import { Prettify } from '@/types';
import {
  GetJobQuery,
  Scalars,
  ListJobCustomerAdminsQuery,
} from '@/types/graphql';
import { handleMutationFormError } from '@/util/error';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import useMediaQuery from '@/hooks/useMediaQuery';

export type Props = {
  job: Prettify<GetJobQuery['job']>;
  hideModal?: () => Scalars['Void'];
};

// Contact type from the GraphQL query
type ContactItem =
  ListJobCustomerAdminsQuery['job']['customer']['admins']['items'][0];

// Create a special type for "Other" option
type ContactOption =
  | ContactItem
  | {
      id: 'other';
      user: { id: 'other'; firstName: 'Other'; lastName: 'Shift Supervisor' };
    };

// Type for custom supervisor data
type CustomSupervisor = {
  name: string;
  phone: string;
  isComplete: boolean;
};

type FormValues = {
  contact: GetJobQuery['job']['contact'];
  contactInstructions: string;
  shareContactNumber: boolean;
};

// Store custom supervisor data globally so it can be accessed by the submit handler
let customSupervisorData: CustomSupervisor | null = null;

// Function to create custom shift supervisor via API
const createCustomShiftSupervisor = async (
  customer_id: string,
): Promise<GetJobQuery['job']['contact'] | null> => {
  if (!customSupervisorData || !customSupervisorData.isComplete) {
    return null;
  }

  try {
    const response = await axiosRubyClient.post('/api/shift_supervisors', {
      name: customSupervisorData.name,
      contact: customSupervisorData.phone,
      customer_id: customer_id,
    });

    const { shift_supervisor } = response.data;

    // Convert the API response to the expected format
    const customContact: GetJobQuery['job']['contact'] = {
      __typename: 'CustomerAdmin',
      id: shift_supervisor.id.toString(),
      avatarUrl: null,
      user: {
        __typename: 'User',
        id: shift_supervisor.id.toString(),
        firstName: customSupervisorData.name,
        lastName: '',
        email: '', // API might not return email for custom supervisors
        middleName: null,
        phoneNumber: customSupervisorData.phone,
      },
    };

    // Clear the custom data after successful creation
    customSupervisorData = null;

    return customContact;
  } catch (error) {
    console.error('Failed to create custom shift supervisor:', error);
    throw error;
  }
};

// Function to check if we have a pending custom supervisor
const hasPendingCustomSupervisor = (): boolean => {
  return customSupervisorData !== null && customSupervisorData.isComplete;
};

const UpdateContactForm = ({ job, hideModal }: Props) => {
  const [fetchAdmins, { data: adminsData, loading: adminsLoading }] =
    useListJobCustomerAdminsLazyQuery();
  const [showOtherForm, setShowOtherForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPhone, setCustomPhone] = useState('');
  const [isOtherFormValid, setIsOtherFormValid] = useState(false);

  // Fetch the admins data when component mounts
  useEffect(() => {
    fetchAdmins({ variables: { jobId: job.id } });
  }, [fetchAdmins, job.id]);

  const admins = adminsData?.job?.customer?.admins?.items || [];
  const phoneOnly = useMediaQuery('(max-width: 559px)');

  // Create the options list with "Other" option at the end
  const contactOptions: ContactOption[] = [
    ...admins,
    ...(!phoneOnly
      ? [
          {
            id: 'other',
            user: {
              id: 'other',
              firstName: 'Other',
              lastName: 'Shift Supervisor',
            },
          },
        ]
      : []),
  ];

  const initialValues: FormValues = {
    contact: job.contact,
    contactInstructions: job.contactInstructions ?? '',
    shareContactNumber: (job as any).shareContactNumber ?? false,
  };

  const [updateJob, { loading }] = useUpdateJobMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(job),
        fields: {
          contact() {},
          contactInstructions() {},
        },
      });
    },
  });

  const updateCustomSupervisorData = (name: string, phone: string) => {
    const hasValidName = name.trim().length > 0;
    const hasValidPhone = phone.length >= 14; // (000) 000-0000 format
    const isComplete = hasValidName && hasValidPhone;

    customSupervisorData = {
      name: name.trim(),
      phone: phone,
      isComplete,
    };

    setIsOtherFormValid(isComplete);
  };

  const handleContactChange = (fieldContext: any) => {
    const contact = fieldContext.value as ContactOption;

    if (contact?.id === 'other') {
      setShowOtherForm(true);
      // Clear any previous custom data
      customSupervisorData = null;
      setIsOtherFormValid(false);
    } else {
      setShowOtherForm(false);
      setCustomName('');
      setCustomPhone('');
      customSupervisorData = null;
      setIsOtherFormValid(true); // Valid when not showing other form
    }
  };

  const handleOtherNameChange = (fieldContext: any) => {
    const name = fieldContext.value;
    setCustomName(name);
    updateCustomSupervisorData(name, customPhone);
  };

  const handleOtherPhoneChange = (fieldContext: any) => {
    const phone = fieldContext.value;
    setCustomPhone(phone);
    updateCustomSupervisorData(customName, phone);
  };

  const handleSubmit = async (
    values: FormValues,
    { setFormError }: SubmitHelpers,
  ): Promise<void> => {
    const jobValues = getJobValues(job);

    try {
      let finalContact = values.contact;

      // If we have a pending custom supervisor, create it first
      if (hasPendingCustomSupervisor() && job.account?.customer?.id) {
        const createdContact = await createCustomShiftSupervisor(
          job.account.customer.id,
        );
        if (createdContact) {
          finalContact = createdContact;
        }
      }

      await updateJob({
        variables: {
          ...jobValues,
          contactId: finalContact.id,
          contactInstructions: values.contactInstructions.trim(),
          shareContactNumber: values.shareContactNumber,
        } as any,
      });

      hideModal?.();
    } catch (err) {
      handleMutationFormError(err, {
        setFormError,
        errorMap: {
          all: (gqlError) => ({
            title: gqlError.name,
            message: gqlError.message,
            status: 'danger',
          }),
        },
      });
    }
  };

  // Determine if submit button should be disabled
  const isSubmitDisabled = loading || (showOtherForm && !isOtherFormValid);

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit}>
      <ItemSelectField<ContactOption>
        required
        disabled={!isJobFieldEditable(job, jobScreenField.shiftSupervisor)}
        callback={handleContactChange}
        fieldId="contact"
        itemToKey={(item) => item.id}
        itemToString={(item) => {
          if (!item) return '';
          if (item.id === 'other') {
            return '+ Other Shift Supervisor';
          }
          return `${item.user.firstName} ${item.user.lastName}`;
        }}
        items={contactOptions}
        label="Shift supervisor"
        placeholder="Select a Shift Supervisor..."
      />

      {showOtherForm && (
        <Stack vertical gap={0} css={{ marginTop: 16 }}>
          <TextField
            required
            autoFocus
            fieldId="otherSupervisorName"
            placeholder="Enter Shift Supervisor name"
            callback={handleOtherNameChange}
          />
          <MaskedInputField
            required
            fieldId="otherSupervisorPhone"
            label="Contact Number"
            mask="(000) 000-0000"
            placeholder="(555) 555-5555"
            type="tel"
            incompletemessage="Must be a valid phone number"
            callback={handleOtherPhoneChange}
          />
        </Stack>
      )}

      <OptionField
        appearance="checkbox"
        fieldId="shareContactNumber"
        label="Share contact number with GravyWorkers"
      />

      <div
        style={{ marginLeft: '32px', marginTop: '-20px', marginBottom: '16px' }}
      >
        <span style={{ fontSize: '14px', color: '#666' }}>
          This will be shared with hired GravyWorkers{' '}
          <strong>90 minutes</strong> before the job starts.
        </span>
      </div>

      <TextAreaField
        disabled={
          !isJobFieldEditable(job, jobScreenField.shiftSupervisorInstructions)
        }
        fieldId="contactInstructions"
        label={
          <>
            <strong>Check-in Instructions (Optional)</strong>
            <br />
            <br />
            These optional instructions will be shared with hired workers{' '}
            <strong>90 minutes</strong> before the job starts to help them check
            in with the shift supervisor at the right time.
          </>
        }
        maxLength={MAX_LENGTH_SHIFT_SUPERVISOR_INSTRUCTIONS}
        placeholder="Provide instructions for how workers should check-in with you on the job site. For example, checkin with your shift supervisor as the hotel lobby. "
      />
      <Stack justify="end">
        <Button
          a11yLabel="Submit form"
          disabled={isSubmitDisabled}
          isLoading={loading}
          label="Update"
          type="submit"
        />
      </Stack>
    </Form>
  );
};

export default UpdateContactForm;
